import React from "react";
import { Link } from "react-router-dom";

import { GlossaryTerm } from "@features/investments/glossary/term";
import { InvestmentsWorkspaceShell } from "@features/investments/components/workspace-shell";
import { InvestmentsLoadingScreen, InvestmentsPageState } from "@features/investments/components/page-state";
import { useInvestmentsAccessQuery } from "@features/investments/queries";
import "@views/investments/index.scss";

const MethodScreen = (): JSX.Element => {
  const accessQuery = useInvestmentsAccessQuery();

  if (accessQuery.isLoading) {
    return <InvestmentsLoadingScreen message="Loading method guide..." />;
  }

  if (accessQuery.error) {
    return <InvestmentsPageState message={`Failed to resolve investments access: ${accessQuery.error.message}`} />;
  }

  return (
    <InvestmentsWorkspaceShell
      title="Method"
      description="How the Investments system thinks, how the tiers differ, and how to interpret the house workflow."
      headerMeta={<div className="dashboard-generated-at">Reference guide</div>}
    >
      <section className="workflow-section">
        <div className="workflow-section-header">
          <div>
            <h2>System Method</h2>
            <p>This is the narrative layer behind the glossary. Use it to understand how Snapshot Research and Operator Mode fit together.</p>
          </div>
        </div>

        <div className="reference-tab-strip" role="tablist" aria-label="Reference sections">
          <Link className="reference-tab-link" to="/investments/reference/glossary">Glossary</Link>
          <span className="reference-tab-link active">Method</span>
          <Link className="reference-tab-link" to="/investments/reference/help">Help</Link>
        </div>

        <div className="reference-method-grid">
          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">How The System Decides</div>
            <h3>Discovery, underwriting, action, and review stay connected</h3>
            <p>
              Coverage is where names are discovered and compared. Detail pages explain the business, the valuation discipline, and the key risk.
              Operator Mode then turns that work into active portfolio decisions and review queues.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Tier Model</div>
            <h3>Snapshot Research vs Operator Mode</h3>
            <p>
              Snapshot Research is cached and precomputed. Operator Mode adds live quote checks, refresh actions, portfolio workflow, and review management.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Equity Workflow</div>
            <h3>Use business quality, price discipline, and risk together</h3>
            <p>
              Equities are judged through <GlossaryTerm label="Score" />, <GlossaryTerm label="Moat Band" />, rates-aware entry discipline, and the house action mapping.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">ETF Workflow</div>
            <h3>ETFs are role-and-exposure decisions, not equity copies</h3>
            <p>
              ETF coverage is for portfolio role, cost, liquidity, and exposure quality. It is intentionally different from the equity underwriting flow.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Actions And Status</div>
            <h3>Statuses track lifecycle, actions track what to do now</h3>
            <p>
              Portfolio stage expresses workflow: active research, candidate, or owned. Recommended action expresses the current decision under the model.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Rates Discipline</div>
            <h3>Treasury hurdles are part of the framework, not background trivia</h3>
            <p>
              Use <GlossaryTerm label="FCF-10Y" /> and related spread measures to judge whether an equity still clears the current macro hurdle rate environment.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Valuation Triangulation</div>
            <h3>House hurdle, comparables, and DCF answer different questions</h3>
            <p>
              <GlossaryTerm label="House Hurdle" /> applies the house rate-aware EV/FCF discipline. <GlossaryTerm label="Trading Comparables" /> shows how peers and the company&apos;s own history trade. DCF Lab tests explicit cash-flow scenarios. Disagreement is evidence to investigate, not a signal to average the outputs.
            </p>
          </section>

          <section className="investments-card workflow-card reference-method-card">
            <div className="section-eyebrow">Comparable Method</div>
            <h3>Capital structure and denominator quality matter</h3>
            <p>
              EV multiples translate peer quartiles through the subject&apos;s net debt before deriving equity value. The lens prefers FCF, then EBITDA, then revenue; revenue multiples are speculative because they ignore profitability. Curated peers take precedence, industry matches come next, and sector peers appear only when the set is otherwise too small. These are trading multiples, not transaction multiples or recommendations.
            </p>
          </section>
        </div>
      </section>
    </InvestmentsWorkspaceShell>
  );
};

export default MethodScreen;
